import { AlertTriangle, ChevronDown, Download, Eye, Square, Trophy, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import { AttemptOutcome } from "@/components/attempt-outcome";
import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { ScoreCell } from "@/components/score-cell";
import { TrajectoryDialog } from "@/components/trajectory-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useRefreshing } from "@/hooks/use-async";
import { useAdminKey } from "@/hooks/use-auth";
import type { AdminChallenge, AdminSimSession } from "@/lib/api";
import { ApiError, adminApi } from "@/lib/api";
import type { StudentAttempts, TeamAttempts } from "@/lib/attempts";
import { attemptNumbers, attemptsCsv, groupAttempts, matchesAttempts } from "@/lib/attempts";
import { formatDegrees, formatError, formatRelative, formatTimestamp } from "@/lib/format";
import { formatPoints, formatSpl } from "@/lib/scoring";
import { useChallenge } from "@/routes/admin/challenge/use-challenge";
import { cn } from "@/lib/utils";

const ACTIVE_POLL_MS = 3000;
const IDLE_POLL_MS = 15000;

function ms(value: number | null): string {
  if (value === null) return "—";
  return value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${value.toFixed(1)}ms`;
}

/**
 * Every attempt at a server-simulated challenge, by team, then by student.
 *
 * The leaderboard row is the team's, so a team is a heading with the run that counts for
 * it; each member is a fold with their best. The attempt budget sits with whoever owns it:
 * the team's for a competition, each student's for practice. Inside are the sessions,
 * every one -- a run that was aborted or timed out has no submission but spent an attempt
 * -- with the verdict, the raw distance (a miss's too, which the student is not told), and
 * both think-time measurements: `Measured` is the gateway's, `Reported` the SDK's, and a
 * run where they disagree is flagged.
 */
export function AdminChallengeAttemptsRoute() {
  const { id = "" } = useParams();
  const adminKey = useAdminKey();
  const { challenge } = useChallenge();
  const [query, setQuery] = useState("");
  const [viewing, setViewing] = useState<string | null>(null);
  const [ending, setEnding] = useState<AdminSimSession | null>(null);
  const [busy, setBusy] = useState(false);

  const sessions = useRefreshing(
    adminKey ? () => adminApi.adminSimSessions(id, adminKey) : null,
    (rows) =>
      rows.some((s) => s.status === "running" || s.status === "pending")
        ? ACTIVE_POLL_MS
        : IDLE_POLL_MS,
    [id, adminKey],
  );

  const sections = useMemo(() => groupAttempts(sessions.data ?? []), [sessions.data]);
  const shown = useMemo(
    () =>
      sections
        .map((team) => ({
          ...team,
          students: team.students.filter((s) => matchesAttempts(s, query)),
        }))
        .filter((team) => team.students.length > 0),
    [sections, query],
  );

  async function end(session: AdminSimSession) {
    if (!adminKey) return;
    setBusy(true);
    try {
      await adminApi.endSimSession(id, session.id, adminKey);
      toast.success("Session ended; the client has been told");
      setEnding(null);
      sessions.refetch();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  function exportCsv() {
    const url = URL.createObjectURL(new Blob([attemptsCsv(sections)], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `attempts-${id}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const all = sessions.data ?? [];
  const running = all.filter((s) => s.status === "running").length;
  const students = sections.reduce((n, t) => n + t.students.length, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by email, name or team"
          className="sm:max-w-xs"
        />
        <Button variant="outline" onClick={exportCsv} disabled={!all.length}>
          <Download className="size-4" aria-hidden />
          Export CSV
        </Button>
        <p className="text-muted-foreground ml-auto text-sm tabular-nums">
          {all.filter((s) => s.status !== "pending").length} attempts · {students} student
          {students === 1 ? "" : "s"}
          {running ? (
            <>
              {" · "}
              <span className="text-primary font-medium">{running} live</span>
            </>
          ) : null}
        </p>
      </div>

      <AsyncBoundary state={sessions}>
        {(rows) =>
          rows.length === 0 ? (
            <EmptyState
              title="No attempts yet"
              description="A student appears here the moment they reserve a session."
            />
          ) : shown.length === 0 ? (
            <EmptyState title="No matches" />
          ) : (
            shown.map((team) => (
              <TeamSection
                key={team.key}
                team={team}
                challenge={challenge}
                filtering={query.trim() !== ""}
                onView={setViewing}
                onEnd={setEnding}
              />
            ))
          )
        }
      </AsyncBoundary>

      <TrajectoryDialog
        jobId={viewing}
        challengeId={id}
        simulator={challenge.simulator}
        onClose={() => setViewing(null)}
      />

      {/* Irreversible for the student -- the attempt is spent, not refunded -- so it asks,
          as the student's own Abort does. */}
      <AlertDialog open={ending !== null} onOpenChange={(next) => !next && !busy && setEnding(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End this session?</AlertDialogTitle>
            <AlertDialogDescription>
              {ending ? `${ending.email}'s run stops where it is` : "The run stops where it is"}{" "}
              and is not scored. The attempt stays spent. The SDK driving it is told and cannot
              continue; the team's slot is freed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Keep running</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => ending && end(ending)}>
              End session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TeamSection({
  team,
  challenge,
  filtering,
  onView,
  onEnd,
}: {
  team: TeamAttempts;
  challenge: AdminChallenge;
  filtering: boolean;
  onView: (jobId: string) => void;
  onEnd: (session: AdminSimSession) => void;
}) {
  const members = team.students.length;
  const shared = challenge.type === "competition";
  const used = team.students.reduce((n, s) => n + s.used, 0);
  return (
    <section className="space-y-2">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b pb-1.5">
        <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight">
          <Users className="size-4 opacity-70" aria-hidden />
          {team.team?.name ?? "No team"}
        </h2>
        <span className="text-muted-foreground text-sm">
          {members} {team.team ? `member${members === 1 ? "" : "s"}` : "on their own"}
        </span>
        {shared && team.team ? (
          <Budget used={used} allowed={challenge.max_attempts} className="text-sm" />
        ) : null}
        {team.team ? (
          <span className="text-muted-foreground ml-auto text-sm">
            {team.best?.result ? (
              <>
                on the leaderboard:{" "}
                <span className="text-foreground font-medium tabular-nums">
                  {formatPoints(team.best.result.score.points)}/
                  {formatPoints(team.best.result.score.max_points)}
                </span>{" "}
                <span className="text-muted-foreground">({team.best.email})</span>
              </>
            ) : (
              "nothing scored yet"
            )}
          </span>
        ) : null}
      </header>
      <div className="space-y-2">
        {team.students.map((student) => (
          <StudentFold
            key={student.key}
            student={student}
            counted={team.team ? team.best : student.best}
            challenge={challenge}
            defaultOpen={student.running > 0 || filtering}
            onView={onView}
            onEnd={onEnd}
          />
        ))}
      </div>
    </section>
  );
}

function StudentFold({
  student,
  counted,
  challenge,
  defaultOpen,
  onView,
  onEnd,
}: {
  student: StudentAttempts;
  /** The run the leaderboard takes from this student's team (or from them, alone). */
  counted: AdminSimSession | null;
  challenge: AdminChallenge;
  defaultOpen: boolean;
  onView: (jobId: string) => void;
  onEnd: (session: AdminSimSession) => void;
}) {
  // The default follows the data; once the reader toggles, their choice wins.
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override ?? defaultOpen;
  const own = challenge.type !== "competition";

  return (
    <Collapsible open={open} onOpenChange={setOverride} className="rounded-lg border">
      <CollapsibleTrigger className="group/student hover:bg-muted/50 flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-sm transition-colors">
        <ChevronDown
          className="text-muted-foreground size-4 shrink-0 transition-transform group-data-[panel-open]/student:rotate-180"
          aria-hidden
        />
        <span className="font-medium">{student.email}</span>
        {student.name ? <span className="text-muted-foreground">{student.name}</span> : null}
        {student.running ? (
          <span className="text-primary flex items-center gap-1.5 text-xs font-medium">
            <span className="relative flex size-2" aria-hidden>
              <span className="bg-primary absolute inline-flex size-full animate-ping rounded-full opacity-60" />
              <span className="bg-primary relative inline-flex size-2 rounded-full" />
            </span>
            live
          </span>
        ) : null}
        {student.suspect ? (
          <AlertTriangle
            className="text-destructive size-3.5"
            aria-label={`${student.suspect} run${student.suspect === 1 ? "" : "s"} with suspect timing`}
          />
        ) : null}
        <span className="ml-auto flex items-center gap-4 tabular-nums">
          {own ? (
            <Budget used={student.used} allowed={challenge.max_attempts} className="text-xs" />
          ) : (
            <span className="text-muted-foreground text-xs">
              {student.used} attempt{student.used === 1 ? "" : "s"}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <span className="text-muted-foreground text-xs">best</span>
            {student.best?.result ? (
              <>
                <AttemptOutcome session={student.best} />
                <ScoreCell
                  points={student.best.result.score.points}
                  max={student.best.result.score.max_points}
                />
              </>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </span>
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <AttemptRows
          student={student}
          counted={counted}
          challenge={challenge}
          onView={onView}
          onEnd={onEnd}
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

/** "2/5 attempts", bold once spent; "2 attempts" when unlimited. */
function Budget({ used, allowed, className }: { used: number; allowed: number | null; className?: string }) {
  const spent = allowed !== null && used >= allowed;
  return (
    <span
      className={cn("text-muted-foreground tabular-nums", spent && "text-foreground font-medium", className)}
    >
      {used}
      {allowed === null ? "" : `/${allowed}`} attempt{used === 1 && allowed === null ? "" : "s"}
    </span>
  );
}

function AttemptRows({
  student,
  counted,
  challenge,
  onView,
  onEnd,
}: {
  student: StudentAttempts;
  counted: AdminSimSession | null;
  challenge: AdminChallenge;
  onView: (jobId: string) => void;
  onEnd: (session: AdminSimSession) => void;
}) {
  const numbers = attemptNumbers(student.sessions);
  const showHeading = challenge.rubric_spec.variables.includes("heading");
  const showSpl = challenge.rubric_spec.variables.includes("spl");

  return (
    <Table className="border-t">
      <TableHeader>
        <TableRow className="bg-muted/40 hover:bg-muted/40">
          <TableHead className="w-12 text-right">#</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Result</TableHead>
          <TableHead className="text-right">Score</TableHead>
          <TableHead className="text-right">Steps</TableHead>
          {showSpl ? <TableHead className="hidden text-right md:table-cell">SPL</TableHead> : null}
          <TableHead className="hidden text-right sm:table-cell">Distance</TableHead>
          {showHeading ? (
            <TableHead className="hidden text-right md:table-cell">Heading</TableHead>
          ) : null}
          <TableHead className="hidden text-right lg:table-cell" title="Think time per step as the gateway measured it">
            Measured
          </TableHead>
          <TableHead className="hidden text-right lg:table-cell" title="Think time per step as the SDK reported it">
            Reported
          </TableHead>
          <TableHead className="hidden text-right xl:table-cell">RTT</TableHead>
          <TableHead className="w-20" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {student.sessions.map((row) => {
          const live = row.status === "running";
          const miss = row.result !== null && !row.result.score.reached;
          return (
            <TableRow key={row.id} className={cn(live && "bg-primary/5")}>
              <TableCell className="text-muted-foreground text-right tabular-nums">
                {numbers.get(row.id) ?? "—"}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <span title={row.sdk_version ? `sdk ${row.sdk_version}` : undefined}>
                  {formatTimestamp(row.started_at)}
                </span>
                <span className="text-muted-foreground hidden text-xs lg:inline">
                  {" · "}
                  {formatRelative(row.started_at)}
                </span>
              </TableCell>
              <TableCell>
                <span className="flex items-center gap-1.5">
                  <AttemptOutcome session={row} />
                  {counted?.id === row.id ? (
                    <Trophy className="text-primary size-3.5" aria-label="Counted for the leaderboard" />
                  ) : null}
                  {!live && row.status !== "scored" && row.end_reason ? (
                    <span className="text-muted-foreground hidden truncate text-xs md:inline">
                      {row.end_reason}
                    </span>
                  ) : null}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <ScoreCell
                  points={row.result?.score.points ?? null}
                  max={row.result?.score.max_points ?? challenge.rubric_spec.max_points}
                />
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {row.steps_used.toLocaleString()}
              </TableCell>
              {showSpl ? (
                <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                  {formatSpl(row.result?.spl ?? null)}
                </TableCell>
              ) : null}
              <TableCell
                className={cn("hidden text-right tabular-nums sm:table-cell", miss && "text-muted-foreground")}
                title={miss ? "A miss; the student is not shown this" : undefined}
              >
                {formatError(row.trans_error ?? null)}
              </TableCell>
              {showHeading ? (
                <TableCell className="text-muted-foreground hidden text-right tabular-nums md:table-cell">
                  {formatDegrees(row.rot_error ?? null)}
                </TableCell>
              ) : null}
              <TableCell className="hidden text-right tabular-nums lg:table-cell">
                <span className="inline-flex items-center gap-1" title={`${row.timing.steps_measured} steps measured`}>
                  {ms(row.timing.think_mean_ms)}
                  {row.timing_suspect ? (
                    <AlertTriangle
                      className="text-destructive size-3.5"
                      aria-label="Reported timing disagrees with the measurement"
                    />
                  ) : null}
                </span>
              </TableCell>
              <TableCell className="text-muted-foreground hidden text-right tabular-nums lg:table-cell">
                {ms(row.timing.reported_mean_ms)}
              </TableCell>
              <TableCell className="text-muted-foreground hidden text-right tabular-nums xl:table-cell">
                {ms(row.rtt_min_ms)}
              </TableCell>
              <TableCell className="text-right">
                {live ? (
                  // Ends the run for real: the worker's session is closed and the SDK is
                  // told. Frees the team's slot; does not refund the attempt.
                  <Button size="sm" variant="ghost" onClick={() => onEnd(row)}>
                    <Square className="size-3.5" aria-hidden />
                    End
                  </Button>
                ) : row.job_id ? (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="View trajectory"
                    onClick={() => onView(row.job_id!)}
                  >
                    <Eye className="size-4" aria-hidden />
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
